import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 700 }]
});

// ─── Design tokens ──────────────────────────────────────────────
const C = {
  primary:      '#1565C0',
  primaryDark:  '#0D47A1',
  primaryLight: '#E3F2FD',
  sectionBg:    '#1976D2',
  subtotalBg:   '#BBDEFB',
  altRow:       '#F5F8FF',
  white:        '#FFFFFF',
  dark:         '#1A1A2E',
  border:       '#B0BEC5',
  green:        '#1B5E20',
  red:          '#B71C1C',
  orange:       '#E65100',
  gray:         '#546E7A',
};

// ─── Column widths ───────────────────────────────────────────────
const COL = {
  sr:        '3%',
  date:      '7%',
  patient:   '16%',
  uhid:      '7%',
  billNo:    '7%',
  payMode:   '7%',
  services:  '16%',
  panel:     '8%',
  billAmt:   '8%',
  disc:      '6%',
  recd:      '8%',
  balance:   '7%',
};

const styles = StyleSheet.create({
  page: { padding: '16pt 18pt', fontFamily: 'Roboto', fontSize: 7, color: C.dark, backgroundColor: C.white },

  // ── Header ──────────────────────────────────────────
  logo:        { width: '100%', height: 64, marginBottom: 2 },
  headerRule:  { height: 2, backgroundColor: C.primary, marginBottom: 4 },
  reportTitle: { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primaryDark, marginBottom: 1, letterSpacing: 1 },
  reportSub:   { fontSize: 7.5, textAlign: 'center', color: C.gray, marginBottom: 6 },

  // ── Table header ────────────────────────────────────
  tableHead: { flexDirection: 'row', backgroundColor: C.sectionBg, borderRadius: 2 },
  th: {
    color: C.white, fontFamily: 'Roboto', fontWeight: 700, fontSize: 6.5,
    paddingVertical: 4, paddingHorizontal: 2,
    textAlign: 'center', borderRightWidth: 0.5, borderRightColor: '#90CAF9',
  },

  // ── Doctor section header ────────────────────────────
  doctorHeader: {
    flexDirection: 'row', backgroundColor: C.primaryLight,
    borderLeftWidth: 3, borderLeftColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6, marginTop: 9, marginBottom: 0,
    alignItems: 'center',
  },
  doctorName:  { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, flex: 1 },
  doctorCount: { fontSize: 6.5, color: C.gray },

  // ── Data rows ────────────────────────────────────────
  row:     { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: C.border },
  rowEven: { backgroundColor: C.white },
  rowOdd:  { backgroundColor: C.altRow },
  td: {
    fontSize: 6.5, paddingVertical: 2.5, paddingHorizontal: 2,
    borderRightWidth: 0.3, borderRightColor: C.border,
  },

  // ── Doctor subtotal ──────────────────────────────────
  doctorSubtotal: {
    flexDirection: 'row', backgroundColor: C.subtotalBg,
    borderTopWidth: 0.5, borderTopColor: C.primary,
    borderBottomWidth: 0.5, borderBottomColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6,
  },
  subtotalLabel: { flex: 1, fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right' },
  subtotalAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', paddingHorizontal: 4 },

  // ── Section divider ──────────────────────────────────
  sectionDivider: {
    backgroundColor: C.sectionBg, paddingVertical: 5, paddingHorizontal: 8,
    marginTop: 14, borderRadius: 2,
  },
  sectionTitle: { fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white, letterSpacing: 0.5 },

  // ── Summary boxes ────────────────────────────────────
  summaryWrap: { marginTop: 14, flexDirection: 'row' },

  modeBox: { flex: 1, borderWidth: 1, borderColor: C.primary, borderRadius: 4, marginRight: 8 },
  modeBoxTitle: {
    backgroundColor: C.primary, color: C.white, fontFamily: 'Roboto', fontWeight: 700,
    fontSize: 7, textAlign: 'center', paddingVertical: 4,
  },
  modeRow: {
    flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border,
    paddingVertical: 3, paddingHorizontal: 6,
  },
  modeRowLast: { flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 6 },
  modeLabel:   { flex: 1, fontSize: 7, color: C.dark },
  modeAmt:     { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', minWidth: 60 },
  modeAmtRed:  { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.red, textAlign: 'right', minWidth: 60 },

  sectionBox: { flex: 1, borderWidth: 1, borderColor: C.primary, borderRadius: 4 },
  sBoxTitle: {
    backgroundColor: C.primary, color: C.white, fontFamily: 'Roboto', fontWeight: 700,
    fontSize: 7, textAlign: 'center', paddingVertical: 4,
  },
  sBoxRow: {
    flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border,
    paddingVertical: 3, paddingHorizontal: 6,
  },
  sBoxRowLast: { flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 6 },
  sBoxLabel:   { flex: 1, fontSize: 7, color: C.dark },
  sBoxAmt:     { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', minWidth: 55 },

  // ── Grand total banner ────────────────────────────────
  grandBox: {
    marginTop: 10, borderWidth: 1.5, borderColor: C.primaryDark, borderRadius: 4,
    backgroundColor: C.primaryDark,
  },
  grandRow: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 10, alignItems: 'center' },
  grandLabel: { flex: 1, fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white },
  grandAmt:   { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F' },

  // ── Credit notice ─────────────────────────────────────
  creditBox: {
    marginTop: 6, borderWidth: 1, borderColor: '#FF8F00', borderRadius: 4,
    backgroundColor: '#FFF8E1',
    flexDirection: 'row', paddingVertical: 4, paddingHorizontal: 10, alignItems: 'center',
  },
  creditLabel: { flex: 1, fontSize: 7.5, color: C.orange },
  creditAmt:   { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.orange },

  // ── Footer ───────────────────────────────────────────
  footer: {
    position: 'absolute', bottom: 10, left: 18, right: 18,
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: C.border, paddingTop: 4,
  },
  footerLeft:  { flex: 1, fontSize: 6, color: C.gray },
  footerRight: { fontSize: 6, color: C.gray, textAlign: 'right' },

  emptyRow:  { paddingVertical: 5, paddingHorizontal: 8 },
  emptyText: { fontSize: 6.5, color: C.gray, fontStyle: 'italic' },
});

// ─── Helpers ──────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return '-';
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}

function fmt(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtINR(n) { return `₹ ${fmt(n)}`; }

function nowStr() {
  const d = new Date();
  return `${fmtDate(d)}  ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ─── Sub-components ───────────────────────────────────────────────
const TH = ({ w, children, align = 'center' }) => (
  <PDFText style={[styles.th, { width: w, textAlign: align }]}>{children}</PDFText>
);

const TD = ({ w, children, align = 'center', bold, color }) => (
  <PDFText style={[
    styles.td,
    { width: w, textAlign: align, color: color || C.dark },
    bold ? { fontFamily: 'Roboto', fontWeight: 700 } : {}
  ]}>
    {children ?? ''}
  </PDFText>
);

const BillingTableHeader = () => (
  <PDFView style={styles.tableHead}>
    <TH w={COL.sr}>#</TH>
    <TH w={COL.date} align="left">Date</TH>
    <TH w={COL.patient} align="left">Patient Name</TH>
    <TH w={COL.uhid}>UHID</TH>
    <TH w={COL.billNo}>Bill / Adm No.</TH>
    <TH w={COL.payMode}>Pay Mode</TH>
    <TH w={COL.services} align="left">Services</TH>
    <TH w={COL.panel} align="left">Panel</TH>
    <TH w={COL.billAmt} align="right">Bill Amt.</TH>
    <TH w={COL.disc} align="right">Discount</TH>
    <TH w={COL.recd} align="right">Recd Amt.</TH>
    <TH w={COL.balance} align="right">Balance</TH>
  </PDFView>
);

// ─── Main Component ───────────────────────────────────────────────
export const CollectionAnalysisReportPdf = ({ data }) => {

  // ── Group by doctor ──
  const groupByDoctor = (rows, nameKey) =>
    (rows || []).reduce((acc, item) => {
      const name = nameKey(item) || 'Unknown Doctor';
      if (!acc[name]) acc[name] = [];
      acc[name].push(item);
      return acc;
    }, {});

  const groupedOpd = groupByDoctor(
    data?.opdBillingData?.data,
    item => item?.patient?.consultant?.name
  );
  const groupedIpd = groupByDoctor(
    data?.indoorServiceData?.data,
    item => item?.consultant?.name
  );

  // ── Per-doctor totals ──
  const doctorTotals = (grouped) =>
    Object.fromEntries(
      Object.entries(grouped).map(([doc, rows]) => [
        doc,
        rows.reduce((s, r) => ({
          bill: s.bill + (r.total_amt || 0),
          disc: s.disc + (r.disc_amt || 0),
          recd: s.recd + (r.total_recd || 0),
          bal:  s.bal  + (r.balance_amt || 0),
        }), { bill: 0, disc: 0, recd: 0, bal: 0 })
      ])
    );

  const opdTotals = doctorTotals(groupedOpd);
  const ipdTotals = doctorTotals(groupedIpd);

  // ── Overall stats from backend (payment-mode aware) ──
  const opdStats  = data?.opdBillingData?.stats    || {};
  const ipdStats  = data?.indoorServiceData?.stats || {};

  const totalCash   = (opdStats.recd_amt_cash   || 0) + (ipdStats.recd_amt_cash   || 0);
  const totalBank   = (opdStats.recd_amt_bank   || 0) + (ipdStats.recd_amt_bank   || 0);
  const totalBoth   = (opdStats.recd_amt_both   || 0) + (ipdStats.recd_amt_both   || 0);
  const totalCredit = (opdStats.recd_amt_credit || 0) + (ipdStats.recd_amt_credit || 0);

  const opdBillTotal = opdStats.bill_amt  || 0;
  const opdRecdTotal = opdStats.recd_amt  || 0;
  const ipdBillTotal = ipdStats.bill_amt  || 0;
  const ipdRecdTotal = ipdStats.recd_amt  || 0;

  const grandBilled   = opdBillTotal + ipdBillTotal;
  const grandReceived = totalCash + totalBank + totalBoth;

  const printTime = nowStr();

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, backgroundColor: 'white' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Collection Analysis Report">
          <PDFPage style={styles.page} size="A4" orientation="landscape" wrap>

            {/* ══ HEADER ══════════════════════════════════════════════ */}
            <PDFImage src="/logo_Hospital_m.jpeg" style={styles.logo} />
            <PDFView style={styles.headerRule} />
            <PDFText style={styles.reportTitle}>COLLECTION ANALYSIS REPORT</PDFText>
            <PDFText style={styles.reportSub}>Doctor-wise OPD & IPD Billing Analysis  |  Printed on: {printTime}</PDFText>
            <PDFView style={[styles.headerRule, { height: 0.5, marginBottom: 6 }]} />

            {/* ══════════════════════════════════════════════════════════
                OPD PROCEDURES — DOCTOR WISE
            ════════════════════════════════════════════════════════════ */}
            <PDFView style={styles.sectionDivider}>
              <PDFText style={styles.sectionTitle}>
                OPD PROCEDURES — DOCTOR WISE  ({data?.opdBillingData?.data?.length || 0} bills)
              </PDFText>
            </PDFView>

            {Object.keys(groupedOpd).length === 0 && (
              <PDFView style={styles.emptyRow}>
                <PDFText style={styles.emptyText}>No OPD billing records found for this period.</PDFText>
              </PDFView>
            )}

            {Object.keys(groupedOpd).map((doctorName, di) => {
              const rows = groupedOpd[doctorName];
              const tot  = opdTotals[doctorName];
              return (
                <PDFView key={di}>
                  <PDFView style={styles.doctorHeader}>
                    <PDFText style={styles.doctorName}>Dr. {doctorName}</PDFText>
                    <PDFText style={styles.doctorCount}>{rows.length} bill{rows.length !== 1 ? 's' : ''}</PDFText>
                  </PDFView>

                  <BillingTableHeader />

                  {rows.map((row, i) => {
                    const isCredit = (row.pay_mode || '').toUpperCase() === 'CREDIT';
                    return (
                      <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                        <TD w={COL.sr} align="center">{i + 1}</TD>
                        <TD w={COL.date} align="left">{fmtDate(row.date)}</TD>
                        <TD w={COL.patient} align="left">
                          {`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}
                        </TD>
                        <TD w={COL.uhid} align="center">{row.uhid || row.patient?.uhid || '-'}</TD>
                        <TD w={COL.billNo} align="center">{row.patient?.op_no || '-'}</TD>
                        <TD w={COL.payMode} align="center" color={isCredit ? C.orange : C.dark}>{row.pay_mode || '-'}</TD>
                        <TD w={COL.services} align="left">
                          {(row.combinedServiceDetails || []).map(s => s.service_name || s.test_name).filter(Boolean).join(', ') || '-'}
                        </TD>
                        <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                        <TD w={COL.billAmt} align="right">{fmt(row.total_amt)}</TD>
                        <TD w={COL.disc} align="right">{row.disc_amt ? fmt(row.disc_amt) : '-'}</TD>
                        <TD w={COL.recd} align="right" bold color={isCredit ? C.orange : C.dark}>
                          {isCredit ? 'CREDIT' : fmt(row.total_recd)}
                        </TD>
                        <TD w={COL.balance} align="right" color={row.balance_amt > 0 ? C.red : C.dark}>
                          {fmt(row.balance_amt)}
                        </TD>
                      </PDFView>
                    );
                  })}

                  <PDFView style={styles.doctorSubtotal}>
                    <PDFText style={styles.subtotalLabel}>
                      Dr. {doctorName} — Bill: {fmtINR(tot.bill)}  |  Disc: {fmtINR(tot.disc)}  |  Recd: {fmtINR(tot.recd)}  |  Bal: {fmtINR(tot.bal)}
                    </PDFText>
                  </PDFView>
                </PDFView>
              );
            })}

            {/* OPD Section Total */}
            {Object.keys(groupedOpd).length > 0 && (
              <PDFView style={[styles.doctorSubtotal, { backgroundColor: C.primaryLight, borderTopWidth: 1, borderTopColor: C.primaryDark }]}>
                <PDFText style={[styles.subtotalLabel, { color: C.primaryDark, fontSize: 7.5 }]}>
                  OPD TOTAL — Billed: {fmtINR(opdBillTotal)}  |  Received: {fmtINR(opdRecdTotal)}
                </PDFText>
              </PDFView>
            )}

            {/* ══════════════════════════════════════════════════════════
                IPD (INDOOR) PROCEDURES — DOCTOR WISE
            ════════════════════════════════════════════════════════════ */}
            <PDFView style={[styles.sectionDivider, { marginTop: 18 }]}>
              <PDFText style={styles.sectionTitle}>
                INDOOR (IPD) PROCEDURES — DOCTOR WISE  ({data?.indoorServiceData?.data?.length || 0} bills)
              </PDFText>
            </PDFView>

            {Object.keys(groupedIpd).length === 0 && (
              <PDFView style={styles.emptyRow}>
                <PDFText style={styles.emptyText}>No IPD billing records found for this period.</PDFText>
              </PDFView>
            )}

            {Object.keys(groupedIpd).map((doctorName, di) => {
              const rows = groupedIpd[doctorName];
              const tot  = ipdTotals[doctorName];
              return (
                <PDFView key={di}>
                  <PDFView style={styles.doctorHeader}>
                    <PDFText style={styles.doctorName}>Dr. {doctorName}</PDFText>
                    <PDFText style={styles.doctorCount}>{rows.length} bill{rows.length !== 1 ? 's' : ''}</PDFText>
                  </PDFView>

                  <BillingTableHeader />

                  {rows.map((row, i) => {
                    const isCredit = (row.pay_mode || '').toUpperCase() === 'CREDIT';
                    return (
                      <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                        <TD w={COL.sr} align="center">{i + 1}</TD>
                        <TD w={COL.date} align="left">{fmtDate(row.date)}</TD>
                        <TD w={COL.patient} align="left">
                          {`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}
                        </TD>
                        <TD w={COL.uhid} align="center">{row.patient?.uhid || '-'}</TD>
                        <TD w={COL.billNo} align="center">{row.ipd_no || '-'}</TD>
                        <TD w={COL.payMode} align="center" color={isCredit ? C.orange : C.dark}>{row.pay_mode || '-'}</TD>
                        <TD w={COL.services} align="left">
                          {(row.combinedServiceDetails || []).map(s => s.service_name || s.test_name).filter(Boolean).join(', ') || '-'}
                        </TD>
                        <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                        <TD w={COL.billAmt} align="right">{fmt(row.total_amt)}</TD>
                        <TD w={COL.disc} align="right">{row.disc_amt ? fmt(row.disc_amt) : '-'}</TD>
                        <TD w={COL.recd} align="right" bold color={isCredit ? C.orange : C.dark}>
                          {isCredit ? 'CREDIT' : fmt(row.total_recd)}
                        </TD>
                        <TD w={COL.balance} align="right" color={row.balance_amt > 0 ? C.red : C.dark}>
                          {fmt(row.balance_amt)}
                        </TD>
                      </PDFView>
                    );
                  })}

                  <PDFView style={styles.doctorSubtotal}>
                    <PDFText style={styles.subtotalLabel}>
                      Dr. {doctorName} — Bill: {fmtINR(tot.bill)}  |  Disc: {fmtINR(tot.disc)}  |  Recd: {fmtINR(tot.recd)}  |  Bal: {fmtINR(tot.bal)}
                    </PDFText>
                  </PDFView>
                </PDFView>
              );
            })}

            {/* IPD Section Total */}
            {Object.keys(groupedIpd).length > 0 && (
              <PDFView style={[styles.doctorSubtotal, { backgroundColor: C.primaryLight, borderTopWidth: 1, borderTopColor: C.primaryDark }]}>
                <PDFText style={[styles.subtotalLabel, { color: C.primaryDark, fontSize: 7.5 }]}>
                  IPD TOTAL — Billed: {fmtINR(ipdBillTotal)}  |  Received: {fmtINR(ipdRecdTotal)}
                </PDFText>
              </PDFView>
            )}

            {/* ══════════════════════════════════════════════════════════
                SUMMARY SECTION
            ════════════════════════════════════════════════════════════ */}
            <PDFView style={[styles.headerRule, { marginTop: 16, marginBottom: 0, height: 1.5 }]} />
            <PDFText style={{ fontSize: 8, fontWeight: 700, color: C.primaryDark, marginTop: 6, marginBottom: 4 }}>
              COLLECTION ANALYSIS SUMMARY
            </PDFText>

            <PDFView style={styles.summaryWrap}>

              {/* Box 1 — Payment Mode Breakdown */}
              <PDFView style={styles.modeBox}>
                <PDFText style={styles.modeBoxTitle}>COLLECTION BY PAYMENT MODE</PDFText>
                <PDFView style={styles.modeRow}>
                  <PDFText style={styles.modeLabel}>Cash Received</PDFText>
                  <PDFText style={styles.modeAmt}>{fmtINR(totalCash)}</PDFText>
                </PDFView>
                <PDFView style={styles.modeRow}>
                  <PDFText style={styles.modeLabel}>Bank / Online Received</PDFText>
                  <PDFText style={styles.modeAmt}>{fmtINR(totalBank)}</PDFText>
                </PDFView>
                <PDFView style={styles.modeRow}>
                  <PDFText style={styles.modeLabel}>Cash + Bank (Both)</PDFText>
                  <PDFText style={styles.modeAmt}>{fmtINR(totalBoth)}</PDFText>
                </PDFView>
                <PDFView style={[styles.modeRowLast, { backgroundColor: '#E8F5E9', borderTopWidth: 1, borderTopColor: C.primary }]}>
                  <PDFText style={[styles.modeLabel, { fontWeight: 700, color: C.green }]}>Total Net Received</PDFText>
                  <PDFText style={[styles.modeAmt, { color: C.green }]}>{fmtINR(grandReceived)}</PDFText>
                </PDFView>
              </PDFView>

              {/* Box 2 — Section Breakdown */}
              <PDFView style={styles.sectionBox}>
                <PDFText style={styles.sBoxTitle}>COLLECTION BY SECTION</PDFText>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>OPD — Total Billed</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(opdBillTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>OPD — Total Received</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(opdRecdTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>IPD — Total Billed</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(ipdBillTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>IPD — Total Received</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(ipdRecdTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={[styles.sBoxLabel, { fontWeight: 700 }]}>Grand Total Billed</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(grandBilled)}</PDFText>
                </PDFView>
                <PDFView style={[styles.sBoxRowLast, { backgroundColor: '#E8F5E9', borderTopWidth: 1, borderTopColor: C.primary }]}>
                  <PDFText style={[styles.sBoxLabel, { fontWeight: 700, color: C.green }]}>Grand Total Received</PDFText>
                  <PDFText style={[styles.sBoxAmt, { color: C.green }]}>{fmtINR(grandReceived)}</PDFText>
                </PDFView>
              </PDFView>

            </PDFView>

            {/* Grand Total Banner */}
            <PDFView style={styles.grandBox}>
              <PDFView style={styles.grandRow}>
                <PDFText style={styles.grandLabel}>
                  GRAND TOTAL RECEIVED  (Cash + Bank + Both — Credit Excluded)
                </PDFText>
                <PDFText style={styles.grandAmt}>{fmtINR(grandReceived)}</PDFText>
              </PDFView>
            </PDFView>

            {/* Credit Outstanding Notice */}
            {totalCredit > 0 && (
              <PDFView style={styles.creditBox}>
                <PDFText style={styles.creditLabel}>
                  * Credit Outstanding (not collected — on account):
                </PDFText>
                <PDFText style={styles.creditAmt}>{fmtINR(totalCredit)}</PDFText>
              </PDFView>
            )}

            {/* ══ PAGE FOOTER ══════════════════════════════════════════ */}
            <PDFView style={styles.footer} fixed>
              <PDFText style={styles.footerLeft}>Collection Analysis Report  |  Printed: {printTime}</PDFText>
              <PDFText style={styles.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </PDFView>

          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default CollectionAnalysisReportPdf;
